import { useLocale } from '@solinkcorp/locale';
import { SimpleFormField } from '@solinkcorp/solink-ui/components/Form';
import { SelectItem } from '@solinkcorp/solink-ui/components/Select';
import { SimpleSelect } from '@solinkcorp/solink-ui/components/SimpleSelect';
import { Alert, AlertDescription } from '@solinkcorp/solink-ui/components/ui/alert';
import React from 'react';
import { type UseFormReturn } from 'react-hook-form';
import { DurationThresholdFields } from './DurationThresholdFields';
import { type WidgetFormData } from './schema';

interface JourneyWidgetSettingsProps {
  form: UseFormReturn<WidgetFormData>;
  hasDayPartThresholds: boolean;
  hasAnyDayParts: boolean;
}

/**
 * Settings specific to the total-journey-time widget: a minutes/seconds default threshold
 * and a window-based time horizon.
 *
 * Intended to be displayed as a child of `WidgetDialog`.
 */
export const JourneyWidgetSettings: React.FC<JourneyWidgetSettingsProps> = ({
  form,
  hasDayPartThresholds,
  hasAnyDayParts,
}) => {
  const locale = useLocale();
  const horizon = form.watch('horizon');
  const showDayPartFallbackWarning =
    horizon === 'currentDayPart' && hasAnyDayParts && !hasDayPartThresholds;

  return (
    <>
      <DurationThresholdFields
        form={form}
        helperText={locale.serviceDisplayMonitor.appliedWhenNoDayPartActive}
      />

      <SimpleFormField
        control={form.control}
        name="horizon"
        label={locale.serviceDisplayMonitor.timeHorizon}
        description={locale.serviceDisplayMonitor.chooseTimeHorizon}
        render={({ field }) => (
          // No 'realtime' option: a journey only exists once the vehicle has left the end
          // zone, so there is no live value to show.
          <SimpleSelect className="w-[100%]" data-testid="sdm-widget-horizon-select" {...field}>
            <SelectItem value="15m">{locale.serviceDisplayMonitor.lastFifteenMinutes}</SelectItem>
            <SelectItem value="1h">{locale.serviceDisplayMonitor.lastSixtyMinutes}</SelectItem>
            <SelectItem value="currentHour">{locale.serviceDisplayMonitor.currentHour}</SelectItem>
            {hasAnyDayParts && (
              <SelectItem value="currentDayPart">
                {locale.serviceDisplayMonitor.currentDayPart}
              </SelectItem>
            )}
            <SelectItem value="currentDay">{locale.serviceDisplayMonitor.currentDay}</SelectItem>
          </SimpleSelect>
        )}
      />
      {showDayPartFallbackWarning && (
        <Alert variant="destructive" className="mt-2 border-border">
          <AlertDescription
            className="text-text-secondary"
            data-testid="day-part-no-association-warning"
          >
            {locale.serviceDisplayMonitor.currentDayPartNoAssociationWarning}
          </AlertDescription>
        </Alert>
      )}
    </>
  );
};
